import { useRef, useState } from "react";
import { TIENDA } from "../data/config";
import { useWhatsApp } from "../hooks/useWhatsApp";
import Boton from "./ui/Boton";
import IconoWhatsApp from "./ui/IconoWhatsApp";
import Reveal from "./ui/Reveal";

const ESTADO_INICIAL = { nombre: "", email: "", mensaje: "" };

// Validacion sencilla de email sin dependencias externas.
const esEmailValido = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const Contacto = () => {
  const { abrirWhatsApp } = useWhatsApp();
  const [valores, setValores] = useState(ESTADO_INICIAL);
  const [errores, setErrores] = useState({});
  // Referencia al contenedor donde se inyecta el feedback de exito en el DOM.
  const feedbackRef = useRef(null);

  const actualizarCampo = (evento) => {
    const { name, value } = evento.target;
    setValores((previos) => ({ ...previos, [name]: value }));
    // Limpia el error del campo a medida que el usuario corrige.
    setErrores((previos) => ({ ...previos, [name]: "" }));
  };

  // Valida todos los campos y devuelve un objeto de errores.
  const validar = () => {
    const nuevosErrores = {};
    if (!valores.nombre.trim()) {
      nuevosErrores.nombre = "Por favor ingresa tu nombre.";
    }
    if (!valores.email.trim()) {
      nuevosErrores.email = "Por favor ingresa tu email.";
    } else if (!esEmailValido(valores.email)) {
      nuevosErrores.email = "El email no parece válido.";
    }
    if (!valores.mensaje.trim()) {
      nuevosErrores.mensaje = "Cuéntanos en qué podemos ayudarte.";
    }
    return nuevosErrores;
  };

  // Crea un mensaje de exito en el DOM usando createElement + appendChild
  // (sin innerHTML), respetando las reglas del proyecto.
  const mostrarExito = () => {
    const contenedor = feedbackRef.current;
    if (!contenedor) {
      return;
    }
    // Limpia mensajes previos sin usar innerHTML.
    while (contenedor.firstChild) {
      contenedor.removeChild(contenedor.firstChild);
    }

    const aviso = document.createElement("p");
    aviso.className =
      "mt-6 rounded-lg border border-forest/40 bg-forest/10 px-5 py-4 text-base font-medium text-forest";
    aviso.setAttribute("role", "status");
    const texto = document.createTextNode(
      "¡Gracias! Te estamos redirigiendo a WhatsApp para completar tu mensaje."
    );
    aviso.appendChild(texto);
    contenedor.appendChild(aviso);
  };

  const alEnviar = (evento) => {
    // Evitamos el envio por defecto del formulario.
    evento.preventDefault();

    const nuevosErrores = validar();
    setErrores(nuevosErrores);
    if (Object.keys(nuevosErrores).length > 0) {
      return;
    }

    // Construimos el mensaje para WhatsApp con los datos del usuario.
    const mensaje = `Hola, soy ${valores.nombre.trim()}. Mi email es ${valores.email.trim()}. ${valores.mensaje.trim()}`;
    mostrarExito();
    abrirWhatsApp(mensaje);
    setValores(ESTADO_INICIAL);
  };

  // Clases compartidas de los inputs con foco en Forest Green.
  const claseInput = (campo) =>
    `w-full rounded-lg border bg-white px-5 py-4 text-base text-ink outline-none transition-colors placeholder:text-ink-variant/60 focus:border-forest focus:ring-2 focus:ring-forest/20 ${
      errores[campo] ? "border-error" : "border-peanut/50"
    }`;

  return (
    <section id="contacto" className="bg-cream py-20 md:py-section">
      <div className="mx-auto max-w-container px-5 md:px-16">
        {/*
          Orden en móvil (order-*):  1=badge+título+descripción  2=formulario  3=iconos de contacto
          Orden en desktop (lg:grid-cols-2): col-izq agrupa 1+3, col-der el formulario con lg:row-span-2
        */}
        <div className="grid gap-10 md:gap-12 lg:grid-cols-2 lg:gap-16">

          {/* 1 — Badge + título + descripción */}
          <Reveal className="order-1">
            <span className="text-xs font-semibold uppercase tracking-label text-secondary">
              Contacto
            </span>
            <h2 className="mt-3 font-literata text-2xl font-bold text-charcoal sm:text-3xl md:text-4xl">
              ¿Tienes una pregunta? Escríbenos
            </h2>
            <p className="mt-4 text-base text-ink-variant sm:text-lg">
              Completa el formulario y te contactaremos por WhatsApp. También
              puedes escribirnos directamente cuando quieras.
            </p>
          </Reveal>

          {/* Formulario */}
          <Reveal delay={120} className="order-2 rounded-xl border border-peanut/40 bg-white p-7 shadow-card sm:p-8 md:p-10 lg:row-span-2 lg:order-none">
            <form onSubmit={alEnviar} noValidate>
              <div className="mb-6">
                <label
                  htmlFor="nombre"
                  className="mb-2 block text-sm font-semibold text-charcoal"
                >
                  Nombre
                </label>
                <input
                  id="nombre"
                  name="nombre"
                  type="text"
                  value={valores.nombre}
                  onChange={actualizarCampo}
                  placeholder="Tu nombre"
                  className={claseInput("nombre")}
                  aria-invalid={Boolean(errores.nombre)}
                  aria-describedby={errores.nombre ? "error-nombre" : undefined}
                />
                {errores.nombre && (
                  <p id="error-nombre" className="mt-2 text-sm text-error">
                    {errores.nombre}
                  </p>
                )}
              </div>

              <div className="mb-6">
                <label
                  htmlFor="email"
                  className="mb-2 block text-sm font-semibold text-charcoal"
                >
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={valores.email}
                  onChange={actualizarCampo}
                  placeholder="tucorreo@ejemplo.com"
                  className={claseInput("email")}
                  aria-invalid={Boolean(errores.email)}
                  aria-describedby={errores.email ? "error-email" : undefined}
                />
                {errores.email && (
                  <p id="error-email" className="mt-2 text-sm text-error">
                    {errores.email}
                  </p>
                )}
              </div>

              <div className="mb-6">
                <label
                  htmlFor="mensaje"
                  className="mb-2 block text-sm font-semibold text-charcoal"
                >
                  Mensaje
                </label>
                <textarea
                  id="mensaje"
                  name="mensaje"
                  rows="4"
                  value={valores.mensaje}
                  onChange={actualizarCampo}
                  placeholder="¿En qué podemos ayudarte?"
                  className={`${claseInput("mensaje")} resize-none`}
                  aria-invalid={Boolean(errores.mensaje)}
                  aria-describedby={errores.mensaje ? "error-mensaje" : undefined}
                />
                {errores.mensaje && (
                  <p id="error-mensaje" className="mt-2 text-sm text-error">
                    {errores.mensaje}
                  </p>
                )}
              </div>

              <Boton type="submit" variante="primario" className="w-full text-lg">
                <IconoWhatsApp className="text-2xl" />
                Enviar por WhatsApp
              </Boton>
            </form>

            {/* Contenedor donde se inyecta el feedback de exito (DOM nativo) */}
            <div ref={feedbackRef} aria-live="polite" />
          </Reveal>

          {/* 3 — Iconos de contacto: order-3 en móvil, segunda fila col-izq en desktop */}
          <Reveal className="order-3">
            <ul className="space-y-5">
              <li className="flex items-start gap-4">
                <img
                  src="https://cdn-icons-png.flaticon.com/512/733/733585.png"
                  alt="Icono WhatsApp"
                  className="mt-1 h-8 w-8"
                  loading="lazy"
                />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-label text-secondary">
                    WhatsApp
                  </p>
                  <p className="text-base text-ink">{TIENDA.whatsapp}</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <img
                  src="https://cdn-icons-png.flaticon.com/512/2111/2111463.png"
                  alt="Icono Instagram"
                  className="mt-1 h-8 w-8"
                  loading="lazy"
                />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-label text-secondary">
                    Instagram
                  </p>
                  <a
                    href={TIENDA.instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-base text-ink transition-colors hover:text-forest"
                  >
                    @{TIENDA.instagram}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <img
                  src="https://cdn-icons-png.flaticon.com/512/535/535239.png"
                  alt="Icono ubicación"
                  className="mt-1 h-8 w-8"
                  loading="lazy"
                />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-label text-secondary">
                    Ciudad
                  </p>
                  <p className="text-base text-ink">{TIENDA.ciudad}</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <img
                  src="https://cdn-icons-png.flaticon.com/512/2989/2989988.png"
                  alt="Icono horario"
                  className="mt-1 h-8 w-8"
                  loading="lazy"
                />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-label text-secondary">
                    Horario
                  </p>
                  <p className="text-base text-ink">{TIENDA.horario}</p>
                </div>
              </li>
            </ul>
          </Reveal>

        </div>
      </div>
    </section>
  );
};

export default Contacto;
